// features/attribute/attributeSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import attributeService from "../../../services/attributeService";

const initialState = {
  attributes: [],
  attribute: null,
  isLoading: false,
  error: null,
  success: null,
};

// 🟢 Async Thunks

export const createAttribute = createAsyncThunk(
  "attribute/createAttribute",
  async (data, thunkAPI) => {
    try {
      return await attributeService.createAttribute(data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getallAttributes = createAsyncThunk(
  "attribute/getallAttributes",
  async (activeOnly = false, thunkAPI) => {
    try {
      return await attributeService.getallAttributes(activeOnly);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getSingleAttribute = createAsyncThunk(
  "attribute/getSingleAttribute",
  async (id, thunkAPI) => {
    try {
      return await attributeService.getSingleAttribute(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateAttribute = createAsyncThunk(
  "attribute/updateAttribute",
  async ({ id, data }, thunkAPI) => {
    try {
      return await attributeService.updateAttribute(id, data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteAttribute = createAsyncThunk(
  "attribute/deleteAttribute",
  async (id, thunkAPI) => {
    try {
      return await attributeService.deleteAttribute(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const activateAttribute = createAsyncThunk(
  "attribute/activateAttribute",
  async (id, thunkAPI) => {
    try {
      return await attributeService.activateAttribute(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);


// 🟣 Slice

const attributeSlice = createSlice({
  name: "attribute",
  initialState,
  reducers: {
    resetAttributeState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = null;
      state.attribute = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(createAttribute.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createAttribute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.attributes.push(action.payload);
        toast.success("Attribute created successfully");
      })
      .addCase(createAttribute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // GET ALL
      .addCase(getallAttributes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getallAttributes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.attributes = action.payload.data || action.payload;
      })
      .addCase(getallAttributes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // GET SINGLE
      .addCase(getSingleAttribute.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleAttribute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.attribute = action.payload;
      })
      .addCase(getSingleAttribute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateAttribute.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateAttribute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.attributes = state.attributes.map((attr) =>
          attr._id === action.payload._id ? action.payload : attr
        );
        toast.success("Attribute updated successfully");
      })
      .addCase(updateAttribute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // DELETE
      .addCase(deleteAttribute.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteAttribute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.attributes = state.attributes.filter(
          (attr) => attr._id !== action.payload._id
        );
        toast.success("Attribute deleted successfully");
      })
      .addCase(deleteAttribute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })
      // ACTIVATE
      .addCase(activateAttribute.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(activateAttribute.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.attributes = state.attributes.map((attr) =>
          attr._id === action.payload._id ? action.payload : attr
        );
        toast.success("Attribute activated successfully");
      })
      .addCase(activateAttribute.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })
      ;
  },
});

export const { resetAttributeState } = attributeSlice.actions;

export const selectAttributes = (state) => state.attribute.attributes;
export const selectAttribute = (state) => state.attribute.attribute;
export default attributeSlice.reducer;